import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { FileText, Upload, X } from "lucide-react";
import React, { useEffect } from "react";

interface FileUploadProps {
  label: string;
  accept: string;
  onChange: (file: File | null) => void;
  value?: File | null; // Added value prop to control the component from parent
  takesFullHeight?: boolean;
}

export function FileUpload({
  label,
  accept,
  onChange,
  value,
  takesFullHeight = false,
}: FileUploadProps) {
  const [dragActive, setDragActive] = React.useState(false);
  const [file, setFile] = React.useState<File | null>(null);

  // Update local state when value prop changes (for persistence between steps)
  useEffect(() => {
    if (value) {
      setFile(value);
    }
  }, [value]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const newFile = e.dataTransfer.files[0];
      setFile(newFile);
      onChange(newFile);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const newFile = e.target.files[0];
      setFile(newFile);
      onChange(newFile);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFile(null);
    onChange(null);

    // Reset the file input value
    const fileInput = document.getElementById(
      `file-${label}`,
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / k ** i).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className={cn("space-y-2", takesFullHeight && "h-full")}>
      <Label>{label}</Label>

      {!file ? (
        // Upload interface when no file is selected
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer",
            dragActive ? "border-primary" : "border-gray-300",
            "hover:border-primary transition-colors",
            takesFullHeight && "h-full",
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept={accept}
            onChange={handleChange}
            className="hidden"
            id={`file-${label}`}
          />
          <label
            htmlFor={`file-${label}`}
            className="cursor-pointer flex flex-col items-center justify-center w-full h-full"
          >
            <Upload className="h-6 w-6 mb-2 text-gray-500" />
            <span className="text-sm font-medium">
              <strong>Klicken</strong>{" "}
              <span className="font-light">
                oder Dateien hierherziehen zum Hochladen
              </span>
            </span>
          </label>
        </div>
      ) : (
        // File preview when a file is selected
        <div className="border rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-100 p-2 rounded">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium truncate max-w-[200px]">
                  {file.name}
                </span>
                <span className="text-xs text-gray-500">
                  {formatFileSize(file.size)}
                </span>
              </div>
            </div>
            <button
              onClick={handleRemoveFile}
              className="p-1 rounded-full hover:bg-gray-100"
              title="Datei entfernen"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
